import React from 'react';
import {
    ComponentsOverrides,
    ComponentsVariants,
    createTheme,
    CSSObject,
    ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { components } from './components';
import { baseTheme } from './base-theme';
import { borderRadius, borderWidth, calculateBorderRadius, spacing } from '../tokens';
import type { OrderedListItemProps, OrderedListProps, UnorderedListItemProps, UnorderedListProps } from '../components';

const theme = createTheme(baseTheme, {
    components,
});

export function ThemeProvider({ children }: { children: React.ReactNode | React.ReactNode[] }) {
    return <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>;
}

// I couldn't tell you why the module augmentation doesn't work in each respective foundation file, but it works here
declare module '@mui/material/styles' {
    interface ComponentNameToClassKey {
        MuiUnorderedList: 'root';
        MuiUnorderedListItem: 'root';
        MuiOrderedList: 'root';
        MuiOrderedListItem: 'root';
    }

    interface ComponentsPropsList {
        MuiUnorderedList: Partial<UnorderedListProps>;
        MuiUnorderedListItem: Partial<UnorderedListItemProps>;
        MuiOrderedList: Partial<OrderedListProps>;
        MuiOrderedListItem: Partial<OrderedListItemProps>;
    }

    interface Components {
        MuiUnorderedList?: {
            defaultProps?: ComponentsPropsList['MuiUnorderedList'];
            styleOverrides?: ComponentsOverrides<Theme>['MuiUnorderedList'];
            variants?: ComponentsVariants['MuiUnorderedList'];
        };
        MuiUnorderedListItem?: {
            defaultProps?: ComponentsPropsList['MuiUnorderedListItem'];
            styleOverrides?: ComponentsOverrides<Theme>['MuiUnorderedListItem'];
            variants?: ComponentsVariants['MuiUnorderedListItem'];
        };
        MuiOrderedList?: {
            defaultProps?: ComponentsPropsList['MuiOrderedList'];
            styleOverrides?: ComponentsOverrides<Theme>['MuiOrderedList'];
            variants?: ComponentsVariants['MuiOrderedList'];
        };
        MuiOrderedListItem?: {
            defaultProps?: ComponentsPropsList['MuiOrderedListItem'];
            styleOverrides?: ComponentsOverrides<Theme>['MuiOrderedListItem'];
            variants?: ComponentsVariants['MuiOrderedListItem'];
        };
    }
    interface TypeText {
        tertiary: string;
    }

    interface TypographyVariants {
        label: CSSObject;
        tooltip: CSSObject;
        helperText: CSSObject;
        chip: CSSObject;
        tab: CSSObject;
    }

    // allow configuration using `createTheme`
    interface TypographyVariantsOptions {
        label: CSSObject;
        tooltip: CSSObject;
        helperText: CSSObject;
        chip: CSSObject;
        tab: CSSObject;
    }

    interface Theme {
        borderRadius: typeof calculateBorderRadius;
        tokens: {
            borderWidth: typeof borderWidth;
            spacing: typeof spacing;
            borderRadius: typeof borderRadius;
        };
    }

    interface TypeBackground {
        hover: React.CSSProperties['color'];
    }
    // allow configuration using `createTheme`
    interface ThemeOptions {
        borderRadius: typeof calculateBorderRadius;
        tokens?: {
            borderWidth?: typeof borderWidth;
            spacing: typeof spacing;
            borderRadius: typeof borderRadius;
        };
    }

    interface Palette {
        border: Omit<Palette['primary'], 'contrastText'>;
        components: {
            backdrop: {
                bgcolor: React.CSSProperties['color'];
            };
            tooltip: {
                bgcolor: React.CSSProperties['color'];
                color: React.CSSProperties['color'];
            };
            outlinedInput: {
                disabled: {
                    bgcolor: React.CSSProperties['color'];
                };
            };
        };
        focusRing: React.CSSProperties['color'];
    }

    interface PaletteOptions {
        border?: Omit<PaletteOptions['primary'], 'contrastText'>;
        components: {
            backdrop: {
                bgcolor: React.CSSProperties['color'];
            };
            tooltip: {
                bgcolor: React.CSSProperties['color'];
                color: React.CSSProperties['color'];
            };
            outlinedInput: {
                disabled: {
                    bgcolor: React.CSSProperties['color'];
                };
            };
        };
        focusRing: React.CSSProperties['color'];
    }
}

declare module '@mui/material/Button' {
    interface ButtonPropsSizeOverrides {
        large: false;
    }
    interface ButtonPropsColorOverrides {
        warning: false;
    }
}

declare module '@mui/material/IconButton' {
    interface IconButtonPropsSizeOverrides {
        large: false;
    }
    interface IconButtonPropsColorOverrides {
        warning: false;
        primary: false;
        default: false;
    }
}

declare module '@mui/material/Checkbox' {
    interface CheckboxPropsSizeOverrides {
        small: false;
    }
    interface CheckboxPropsColorOverrides {
        primary: false;
        warning: false;
        inherit: false;
        default: false;
    }
}
